import React, { useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { isSystemAdminRole } from '@/lib/roles';
import { isFeatureEnabled } from '@/config/featureFlags';
import { useResolvedFeatureFlags } from '@/hooks/useResolvedFeatureFlags';
import { BELOW_CHROME_HEIGHT } from '@/components/GlobalHeader';
import {
  activeAdminSection,
  isAdminPageActive,
  visibleAdminSections,
} from '@/lib/adminNav';
import { useMyAdminModuleAccess } from '@/lib/adminModuleAccess';

interface AdminSectionSidebarProps {
  /**
   * AR-316 pattern: render for the mobile nav drawer — always expanded, full
   * width, no collapse control, and taller rows for touch.
   */
  inDrawer?: boolean;
  /** Called after a nav row navigates, so the drawer can close itself. */
  onNavigated?: () => void;
}

/**
 * AdminSectionSidebar is the Admin area's secondary navigation column (AR-285).
 *
 * It lists the pages of the section selected in the primary sidebar, using the
 * same text-only secondary-menu standard as FinancesSidebar.
 */
export const AdminSectionSidebar: React.FC<AdminSectionSidebarProps> = ({
  inDrawer = false,
  onNavigated,
}) => {
  const [location, navigate] = useLocation();
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const { isEnabled: isResolvedEnabled, isLoading: flagsLoading } = useResolvedFeatureFlags();

  const advancedFlagEnabled = isFeatureEnabled('agenticAdminAdvanced');
  const financesEnabled = !flagsLoading && isResolvedEnabled('invoicingMvp');
  const { moduleAccess } = useMyAdminModuleAccess();
  const onboardingEnabled = !flagsLoading && isResolvedEnabled('onboarding');
  const sections = useMemo(
    () =>
      visibleAdminSections({
        isSystemAdmin: isSystemAdminRole(user?.role),
        advancedFlagEnabled,
        financesEnabled,
        onboardingEnabled,
        moduleAccess,
      }),
    [user?.role, advancedFlagEnabled, financesEnabled, onboardingEnabled, moduleAccess],
  );

  const section = activeAdminSection(location, sections);

  // Inside the mobile drawer the sheet owns the height.
  const sidebarHeight = inDrawer ? undefined : BELOW_CHROME_HEIGHT;

  if (!section) {
    return null;
  }

  if (collapsed && !inDrawer) {
    return (
      <div
        data-app-chrome
        className="flex w-8 flex-col items-center border-r border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 pt-4"
        style={{ height: sidebarHeight }}
      >
        <button
          onClick={() => setCollapsed(false)}
          className="rounded p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
          title={`Expand ${section.label} sidebar`}
          aria-label={`Expand ${section.label} sidebar`}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      data-app-chrome
      data-admin-section={section.id}
      className={cn(
        'flex flex-col bg-gray-50/50 dark:bg-gray-900/50',
        inDrawer ? 'w-full' : 'w-56 border-r border-gray-200 dark:border-gray-700',
      )}
      style={{ height: sidebarHeight }}
    >
      {/* Header — collapse chevron + section title */}
      <div className={cn('px-3 pb-2', inDrawer ? 'pt-3' : 'pt-4')}>
        <div className="flex items-center gap-1.5">
          {!inDrawer && (
            <button
              onClick={() => setCollapsed(true)}
              className="rounded p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
              title="Collapse sidebar"
              aria-label="Collapse sidebar"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 tracking-tight">
            {section.label}
          </p>
        </div>
      </div>

      {/* Page links — text only (secondary menu standard) */}
      <nav
        aria-label={`${section.label} pages`}
        className={cn(
          'px-3 py-2 space-y-0.5',
          !inDrawer && 'flex-1 overflow-y-auto',
        )}
      >
        {section.pages.map((page) => {
          const active = isAdminPageActive(location, page);

          return (
            <button
              key={page.path}
              onClick={() => {
                if (page.disabled) return;
                navigate(page.path);
                onNavigated?.();
              }}
              disabled={page.disabled}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'flex w-full items-center rounded-lg px-3 text-sm text-left transition-colors',
                inDrawer ? 'min-h-11 py-2.5' : 'py-2',
                page.disabled
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                  : active
                    ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 font-medium'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200',
              )}
            >
              <span>{page.label}</span>
              {page.comingSoon && (
                <span className="ml-auto text-xs text-gray-300 dark:text-gray-600">Soon</span>
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default AdminSectionSidebar;
